import { useCallback, useEffect, useId, useRef, useState } from 'react';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { Box, Button, CircularProgress, Stack } from '@mui/material';
import { useEventTracking } from '@services/statsig';

import type { GeneratedIntakeGroupHelp } from '../../types';
import type { IntakeDocumentDetail } from '../intakeDocumentDetail';
import { IntakeDocumentDetailDialog } from '../IntakeDocumentDetailDialog';

import { GroupHelpTrigger, HelpPanelDrawer } from './HelpPanelDrawer';
import { IntakePageFooter } from './IntakePageFooter';
import { IntakePageHeader } from './IntakePageHeader';
import { cardSx, chipSx, groupTitleSx, wf } from './tokens';
import { useGroupHelpPanel } from './useGroupHelpPanel';

function getFileType(filename: string): string {
  const extension = filename.split('.').pop()?.toLowerCase();
  return extension && extension !== filename.toLowerCase() ? extension : 'unknown';
}

export interface FoundDocument {
  artifactId: string | null;
  filename: string;
  status: string;
  docKind: string | null;
  filledCount: number;
  understood: boolean;
  detail?: IntakeDocumentDetail;
}

/** Per-required-document satisfaction status shown in the "Documents needed" list. */
export interface DocumentRequirementStatus {
  key: string;
  label: string;
  satisfied: boolean;
  satisfiedByFilename?: string;
  /** The uploaded document that satisfied this requirement, if resolvable — lets the row open the same preview dialog the "Documents received" list uses. */
  satisfiedDocument?: FoundDocument;
}

interface IntakeDocumentsPageProps {
  title: string;
  sub: string;
  dropHint: string;
  documents: FoundDocument[];
  isDragging: boolean;
  isProcessing?: boolean;
  onFiles: (files: File[]) => void;
  onContinue: () => void;
  onBack?: () => void;
  nextLabel: string;
  /** Plain-words summary of where document suggestions can be reviewed. */
  landedSummary?: string | null;
  onReview?: () => void;
  reviewLabel?: string;
  groups?: GeneratedIntakeGroupHelp[];
  onHelpAction?: (action: 'message_specialist') => void;
  getDocumentUrl?: (artifactId: string) => Promise<string>;
  onDismissFailed?: (artifactId: string) => void;
  embedded?: boolean;
  missingRequiredDocumentCount?: number;
  readOnly?: boolean;
  /** Per-required-document satisfaction, driven by the backend's `is_complete`. */
  requirements?: DocumentRequirementStatus[];
}

export function IntakeDocumentsPage({
  title,
  sub,
  dropHint,
  documents,
  isDragging,
  isProcessing = false,
  onFiles,
  onContinue,
  onBack,
  nextLabel,
  landedSummary,
  onReview,
  reviewLabel,
  groups = [],
  onHelpAction = () => undefined,
  getDocumentUrl: resolveDocumentUrl,
  onDismissFailed,
  embedded = false,
  missingRequiredDocumentCount = 0,
  readOnly = false,
  requirements = [],
}: IntakeDocumentsPageProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const folderInputRef = useRef<HTMLInputElement | null>(null);
  const { intakeHub, error: trackError } = useEventTracking();
  const [selectedDocument, setSelectedDocument] = useState<FoundDocument | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const { selectedGroup, selectedGroupId, openGroup, closeGroup } = useGroupHelpPanel(groups);
  const drawerId = `intake-document-group-help-${useId().replace(/:/g, '')}`;

  useEffect(() => {
    folderInputRef.current?.setAttribute('webkitdirectory', '');
    folderInputRef.current?.setAttribute('directory', '');
  }, []);

  const getDocumentUrl = useCallback(
    async (document: FoundDocument): Promise<string | null> => {
      if (!document.artifactId) return null;
      try {
        if (!resolveDocumentUrl) return null;
        return await resolveDocumentUrl(document.artifactId);
      } catch {
        trackError.track('artifact_url_request_failed', 'intake_hub_document_viewer', {
          artifact_id: document.artifactId,
          file_type: getFileType(document.filename),
        });
        return null;
      }
    },
    [resolveDocumentUrl, trackError],
  );

  const handleOpenDocument = useCallback(
    async (document: FoundDocument) => {
      setSelectedDocument(document);
      setPreviewUrl(null);
      intakeHub.documentPreviewOpened({
        artifact_id: document.artifactId ?? 'unavailable',
        file_type: getFileType(document.filename),
      });

      if (!document.artifactId) {
        return;
      }

      setIsPreviewLoading(true);
      const url = await getDocumentUrl(document);
      setPreviewUrl(url);
      setIsPreviewLoading(false);
    },
    [getDocumentUrl, intakeHub],
  );

  const handleOpenFile = useCallback(
    async (artifactId?: string | null) => {
      if (!artifactId || !selectedDocument) return;
      intakeHub.documentDownloadClicked({
        artifact_id: artifactId,
        file_type: getFileType(selectedDocument.filename),
      });
      const url = previewUrl ?? (await getDocumentUrl(selectedDocument));
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    },
    [getDocumentUrl, intakeHub, previewUrl, selectedDocument],
  );

  const closeDocument = () => {
    setSelectedDocument(null);
    setPreviewUrl(null);
  };

  const selectedDetail: IntakeDocumentDetail | null = selectedDocument
    ? (selectedDocument.detail ?? {
        id: selectedDocument.artifactId ?? selectedDocument.filename,
        sourceArtifactId: selectedDocument.artifactId,
        title: selectedDocument.filename,
        docKind: selectedDocument.docKind,
      })
    : null;

  return (
    <>
      {!embedded && <IntakePageHeader title={title} sub={sub} />}

      {groups.length > 0 && (
        <Box sx={cardSx}>
          <Box sx={{ ...groupTitleSx, mb: 1 }}>What to upload</Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
            {groups.map(group => (
              <Box
                key={group.group_id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  border: `1px solid ${wf.lineSoft}`,
                  borderRadius: '6px',
                  px: 1.25,
                  py: 0.75,
                  fontSize: '12.5px',
                }}
              >
                <span>{group.group_name}</span>
                {group.help_panel && (
                  <GroupHelpTrigger
                    groupName={group.group_name}
                    controlsId={drawerId}
                    expanded={selectedGroupId === group.group_id}
                    onOpen={opener => openGroup(group.group_id, opener)}
                  />
                )}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {requirements.length > 0 && (
        <Box sx={cardSx} aria-label="Documents needed">
          <Box sx={{ ...groupTitleSx, mb: 1 }}>Documents needed</Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {requirements.map(requirement => {
              const chip = (
                <Box sx={chipSx(requirement.satisfied ? 'ok' : 'mut')}>
                  {requirement.satisfied
                    ? requirement.satisfiedByFilename
                      ? `Met by ${requirement.satisfiedByFilename}`
                      : 'Received'
                    : 'Needed'}
                </Box>
              );
              const satisfiedDocument = requirement.satisfiedDocument;
              if (requirement.satisfied && satisfiedDocument) {
                return (
                  <Box
                    key={requirement.key}
                    component="button"
                    type="button"
                    aria-label={`View ${satisfiedDocument.filename}`}
                    onClick={() => void handleOpenDocument(satisfiedDocument)}
                    sx={{
                      width: '100%',
                      textAlign: 'left',
                      color: 'inherit',
                      background: 'transparent',
                      border: 0,
                      borderRadius: '6px',
                      padding: '2px 4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      fontSize: '12.5px',
                      cursor: 'pointer',
                      transition: 'background-color 120ms ease, box-shadow 120ms ease',
                      '&:hover': { background: wf.surfaceAlt },
                      '&:focus-visible': {
                        outline: `2px solid ${wf.blue}`,
                        outlineOffset: '2px',
                      },
                    }}
                  >
                    <span>{requirement.label}</span>
                    {chip}
                  </Box>
                );
              }
              return (
                <Box
                  key={requirement.key}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    fontSize: '12.5px',
                  }}
                >
                  <span>{requirement.label}</span>
                  {chip}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {!readOnly && (
        <Box
          sx={{
            border: `2px dashed ${isDragging ? wf.blue : wf.line}`,
            borderRadius: '10px',
            padding: '26px',
            textAlign: 'center',
            color: wf.muted,
            fontSize: '13px',
            background: isDragging ? wf.blueSoft : wf.surfaceAlt,
          }}
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            hidden
            accept="application/pdf,image/jpeg,image/png,image/gif,image/webp"
            onChange={event => {
              const files = Array.from(event.target.files ?? []);
              if (files.length > 0) onFiles(files);
              event.target.value = '';
            }}
          />
          <input
            ref={folderInputRef}
            type="file"
            multiple
            hidden
            data-testid="intake-folder-input"
            accept="application/pdf,image/jpeg,image/png,image/gif,image/webp"
            onChange={event => {
              const files = Array.from(event.target.files ?? []);
              if (files.length > 0) onFiles(files);
              event.target.value = '';
            }}
          />
          <Box>Drag documents or a folder here</Box>
          <Box sx={{ fontSize: '11.5px', marginTop: '4px' }}>{dropHint}</Box>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{ justifyContent: 'center', mt: 1.5 }}
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={<UploadFileOutlinedIcon />}
              onClick={() => inputRef.current?.click()}
              sx={{ textTransform: 'none' }}
            >
              Choose files
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<CreateNewFolderOutlinedIcon />}
              onClick={() => {
                intakeHub.folderUploadClicked({ surface: 'document_canvas' });
                folderInputRef.current?.click();
              }}
              sx={{ textTransform: 'none' }}
            >
              Choose folder
            </Button>
          </Stack>
        </Box>
      )}

      {embedded && missingRequiredDocumentCount > 0 && (
        <Box role="alert" sx={{ color: 'error.main', fontSize: '0.75rem' }}>
          {missingRequiredDocumentCount === 1
            ? '1 required document is missing.'
            : `${missingRequiredDocumentCount} required documents are missing.`}
        </Box>
      )}

      {landedSummary && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
            border: `1px solid ${wf.green}`,
            background: wf.greenSoft,
            color: wf.green,
            borderRadius: '8px',
            padding: '10px 12px',
            fontSize: '12.5px',
          }}
        >
          <span>
            We found <strong>{landedSummary}</strong>. Review each value before moving on.
          </span>
          {onReview && reviewLabel && (
            <Box
              role="button"
              tabIndex={0}
              onClick={onReview}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onReview();
                }
              }}
              sx={{
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                borderRadius: '7px',
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: 650,
                background: wf.green,
                color: '#fff',
              }}
            >
              {reviewLabel}
            </Box>
          )}
        </Box>
      )}

      {documents.length > 0 && (
        <Box sx={cardSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
            <Box sx={groupTitleSx}>Documents received</Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {documents.map(document => {
              const artifactId = document.artifactId;

              return (
                <Box
                  key={`${document.artifactId ?? 'unavailable'}-${document.filename}`}
                  sx={{
                    width: '100%',
                    border: `1px solid ${wf.line}`,
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <Box
                    component="button"
                    type="button"
                    aria-label={`View ${document.filename}`}
                    onClick={() => void handleOpenDocument(document)}
                    sx={{
                      flex: 1,
                      textAlign: 'left',
                      color: 'inherit',
                      background: 'transparent',
                      border: 0,
                      padding: '7px 10px',
                      fontSize: '12.5px',
                      minHeight: '34px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '8px',
                      cursor: 'pointer',
                      transition: 'background-color 120ms ease, box-shadow 120ms ease',
                      '&:hover': { background: wf.surfaceAlt },
                      '&:focus-visible': {
                        outline: `2px solid ${wf.blue}`,
                        outlineOffset: '2px',
                      },
                    }}
                  >
                    <Box component="span">
                      <Box
                        component="span"
                        sx={{ color: wf.muted, fontSize: '10.5px', display: 'block' }}
                      >
                        {document.filename}
                      </Box>
                      {document.status === 'failed' ? (
                        <Box component="span" sx={{ color: 'error.main' }}>
                          Upload failed. Try uploading this file again.
                        </Box>
                      ) : document.docKind ? (
                        document.docKind.replace(/_/g, ' ')
                      ) : document.understood ? (
                        <Stack
                          component="span"
                          direction="row"
                          spacing={0.75}
                          sx={{ alignItems: 'center' }}
                        >
                          <CircularProgress
                            size={12}
                            thickness={5}
                            aria-label={`Reading ${document.filename}`}
                          />
                          <span>Reading this document</span>
                        </Stack>
                      ) : (
                        "We couldn't tell what this is"
                      )}
                    </Box>
                    {document.status === 'failed' ? (
                      <Box sx={chipSx('rev')}>failed</Box>
                    ) : document.understood ? (
                      <Box sx={chipSx('pend')}>{document.status}</Box>
                    ) : (
                      <Box sx={chipSx('rev')}>choose a slot</Box>
                    )}
                  </Box>
                  {document.status === 'failed' && artifactId && onDismissFailed && (
                    <Button
                      size="small"
                      color="error"
                      onClick={() => onDismissFailed(artifactId)}
                      sx={{ mr: 0.5, textTransform: 'none' }}
                    >
                      Dismiss
                    </Button>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {landedSummary && (
        <Box
          sx={{
            fontSize: '11.5px',
            color: wf.muted,
            background: wf.surfaceAlt,
            borderRadius: '6px',
            padding: '8px 10px',
          }}
        >
          Fields we fill from documents show as{' '}
          <Box component="span" sx={{ ...chipSx('sug'), verticalAlign: 'middle' }}>
            suggested
          </Box>{' '}
          — you confirm them on the next pages, or accept a whole page at once.
        </Box>
      )}

      {!embedded && (
        <IntakePageFooter
          primaryLabel={nextLabel}
          onPrimary={onContinue}
          onBack={onBack}
          disabled={isProcessing}
          disabledReason={
            isProcessing
              ? 'Wait for your documents to finish processing before continuing.'
              : undefined
          }
        />
      )}
      <HelpPanelDrawer
        drawerId={drawerId}
        panel={selectedGroup?.help_panel ?? null}
        open={Boolean(selectedGroup?.help_panel)}
        onClose={closeGroup}
        onAction={onHelpAction}
      />
      <IntakeDocumentDetailDialog
        open={Boolean(selectedDocument)}
        document={selectedDetail}
        previewUrl={previewUrl}
        isPreviewLoading={isPreviewLoading}
        onOpenFile={handleOpenFile}
        onClose={closeDocument}
      />
    </>
  );
}
